import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  DocumentCheckIcon,
  LockClosedIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const Compliance = () => {
  const { t } = useTranslation();

  const complianceItems = [
    {
      title: t('compliance.qualifications.hk.title'),
      description: t('compliance.qualifications.hk.description'),
      icon: <ShieldCheckIcon className="w-12 h-12 text-primary" />,
    },
    {
      title: t('compliance.qualifications.us.title'),
      description: t('compliance.qualifications.us.description'),
      icon: <ShieldCheckIcon className="w-12 h-12 text-primary" />,
    },
    {
      title: t('compliance.qualifications.sg.title'),
      description: t('compliance.qualifications.sg.description'),
      icon: <ShieldCheckIcon className="w-12 h-12 text-primary" />,
    },
  ];

  const securityMeasures = [
    {
      title: t('compliance.security.funds.title'),
      description: t('compliance.security.funds.description'),
      features: [
        t('compliance.security.funds.features.multisig'),
        t('compliance.security.funds.features.coldWallet'),
        t('compliance.security.funds.features.audit'),
        t('compliance.security.funds.features.isolation'),
      ],
    },
    {
      title: t('compliance.security.trading.title'),
      description: t('compliance.security.trading.description'),
      features: [
        t('compliance.security.trading.features.monitoring'),
        t('compliance.security.trading.features.limits'),
        t('compliance.security.trading.features.alerts'),
        t('compliance.security.trading.features.system'),
      ],
    },
    {
      title: t('compliance.security.system.title'),
      description: t('compliance.security.system.description'),
      features: [
        t('compliance.security.system.features.ddos'),
        t('compliance.security.system.features.ssl'),
        t('compliance.security.system.features.access'),
        t('compliance.security.system.features.securityAudit'),
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('compliance.title')} - 9527 Finance</title>
        <meta name="description" content={t('compliance.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              className="text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t('compliance.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('compliance.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">{t('compliance.qualifications.title')}</h2>
            <p className="text-xl text-gray-600">
              {t('compliance.qualifications.subtitle')}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {complianceItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                {item.icon}
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Measures */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('compliance.security.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {securityMeasures.map((measure, index) => (
              <motion.div
                key={measure.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                <h3 className="text-xl font-bold mb-4">{measure.title}</h3>
                <p className="text-gray-600 mb-6">{measure.description}</p>
                <ul className="space-y-3">
                  {measure.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Management */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('compliance.riskManagement.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-6">{t('compliance.riskManagement.riskSystem.title')}</h3>
              <p className="text-gray-600 mb-6">
                {t('compliance.riskManagement.riskSystem.description')}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.riskSystem.features.monitoring')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.riskSystem.features.alerts')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.riskSystem.features.reports')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.riskSystem.features.emergency')}</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-6">{t('compliance.riskManagement.team.title')}</h3>
              <p className="text-gray-600 mb-6">
                {t('compliance.riskManagement.team.description')}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.team.features.experts')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.team.features.training')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.team.features.updates')}</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></span>
                  <span>{t('compliance.riskManagement.team.features.advisors')}</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Compliance; 